import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { Eyebrow } from "../components/Bits";
import { BusinessCard } from "../components/directory/BusinessCard";
import { FilterBar } from "../components/directory/FilterBar";
import { Pagination } from "../components/directory/Pagination";
import { LoadingGrid, StateBox, OsmAttribution } from "../components/directory/DirectoryStates";
import { SECTIONS, directoryApi, localName } from "../lib/directory";

const KEYS = ["category", "neighborhood", "cuisine"];

function useFacets(section) {
  const [facets, setFacets] = useState({ categories: [], neighborhoods: [], cuisines: null });
  useEffect(() => {
    Promise.all([directoryApi.categories(section), directoryApi.neighborhoods(section),
      section === "restaurants" ? directoryApi.cuisines(section) : Promise.resolve(null)])
      .then(([categories, neighborhoods, cuisines]) => setFacets({ categories, neighborhoods, cuisines }))
      .catch(() => setFacets({ categories: [], neighborhoods: [], cuisines: null }));
  }, [section]);
  return facets;
}

export default function DirectoryList({ section }) {
  const { t, lang } = useLang();
  const cfg = SECTIONS[section];
  const Icon = cfg.icon;
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""])), [params]);
  const page = Number(params.get("page") || 1);
  const [query, setQuery] = useState(params.get("q") || "");
  const [state, setState] = useState({ status: "loading", data: null });
  const facets = useFacets(section);

  const update = useCallback((patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    const id = setTimeout(() => { if ((params.get("q") || "") !== query.trim()) update({ q: query.trim() }); }, 350);
    return () => clearTimeout(id);
  }, [query, params, update]);

  const load = useCallback(() => {
    setState((s) => ({ ...s, status: "loading" }));
    directoryApi.businesses({ section, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), q: params.get("q") || undefined, page })
      .then((data) => setState({ status: "ok", data }))
      .catch(() => setState({ status: "error", data: null }));
  }, [section, filters, params, page]);

  useEffect(load, [load]);

  const catName = (slug) => localName(facets.categories.find((c) => c.slug === slug)?.name, lang);
  const data = state.data;

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14" data-testid={`directory-page-${section}`}>
      <div className="flex items-start gap-4">
        <div className="hidden sm:flex w-12 h-12 rounded-xl bg-gold/10 text-gold items-center justify-center shrink-0"><Icon size={22} /></div>
        <div>
          <Eyebrow>{t("directory.eyebrow")}</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl text-white mt-2" data-testid="directory-title">{t(`directory.sections.${section}.title`)}</h1>
          <p className="text-white/60 mt-3 max-w-2xl">{t(`directory.sections.${section}.sub`)}</p>
          <OsmAttribution className="mt-3" />
        </div>
      </div>

      <div className="mt-10">
        <FilterBar filters={filters} setFilter={(k, v) => update({ [k]: v })} query={query} setQuery={setQuery}
          categories={facets.categories} neighborhoods={facets.neighborhoods} cuisines={facets.cuisines}
          onClear={() => { setQuery(""); setParams(new URLSearchParams(), { replace: true }); }} />
      </div>

      <div className="mt-8">
        {state.status === "ok" && (
          <div className="text-sm text-white/50 mb-4" data-testid="directory-result-count">
            {data.total} {data.total === 1 ? t("directory.result") : t("directory.results")}
          </div>
        )}
        {state.status === "loading" && <LoadingGrid />}
        {state.status === "error" && <StateBox kind="error" title={t("directory.error_h")} body={t("directory.error_p")} onRetry={load} />}
        {state.status === "ok" && data.items.length === 0 && data.total > 0 && (
          <StateBox kind="pageout" title={t("directory.page_out_h")} body={t("directory.page_out_p")} actionLabel={t("directory.first_page")}
            onRetry={() => update({ page: "" })} />
        )}
        {state.status === "ok" && data.total === 0 && !params.toString() && (
          <StateBox kind="soon" title={t("directory.soon_h")} body={t("directory.soon_p")} />
        )}
        {state.status === "ok" && data.total === 0 && !!params.toString() && (
          <StateBox kind="empty" title={t("directory.empty_h")} body={t("directory.empty_p")} />
        )}
        {state.status === "ok" && data.items.length > 0 && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-results">
              {data.items.map((b) => <BusinessCard key={b.slug} b={b} categoryName={catName(b.category)} />)}
            </div>
            <Pagination page={data.page} pages={data.pages} onPage={(p) => { update({ page: String(p) }); window.scrollTo(0, 0); }} />
          </>
        )}
      </div>
    </div>
  );
}
